import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { RobotSiteHierarchyChips } from '@/components/RobotSiteHierarchyChips';
import { buildRobotSiteDisplayRows, type RobotSiteRow } from '@/lib/robotSiteTree';

type CompletedRobotChip = { id: number; name: string };

type TaskRow = {
  id: number;
  site_id?: number;
  entry_id: number;
  type_key?: string;
  type_label?: string;
  completed_robot_ids: number[];
  completed_robots?: CompletedRobotChip[];
  completed_groups_robot_ids?: number[];
  completed_groups_robots?: CompletedRobotChip[];
  completed_moments_robot_ids?: number[];
  completed_moments_robots?: CompletedRobotChip[];
  title_text: string;
  body_text: string;
  serial_number: string;
  created_at: string;
  /** 小程序 # 短链；与公众号跟圈发圈无关 */
  miniprogram_hash_link?: string;
};

type ListTasksResp = {
  ok: boolean;
  items: TaskRow[];
  total: number;
  page: number;
  per_page: number;
  q?: string;
  includes_child_sites?: boolean;
  scope_site_ids?: number[];
};

type PickerEntry = {
  id: number;
  serial_number: string | null;
  title_text: string | null;
  body_text: string | null;
  created_at: string;
  type_key?: string;
  type_label?: string;
};

type PickerResp = { ok: boolean; items: PickerEntry[]; total: number; page: number; per_page: number };

type MpBriefResp = {
  ok: boolean;
  miniprogram?: {
    id: number;
    name: string;
    site_ids: number[];
    entry_site_options?: Array<{ id: number; site_name: string }>;
  };
};

type RobotSummary = {
  site_id: number;
  robot_module_enabled?: boolean;
  forward_task_count: number;
  forward_task_count_aggregate?: number;
};

function forwardTaskErrMessage(err?: string, detail?: string): string {
  if (err === 'robot_module_disabled') {
    return (
      detail ||
      '本站点在本小程序下未开启「转发模块」。请返回小程序机器人管理页，对该站点点击「开启转发」后再加入队列。'
    );
  }
  return detail?.trim() || err || '加入失败';
}

function entryTypeBadge(label?: string, key?: string) {
  const text = (label || key || '').trim();
  if (!text) {
    return <span className="text-slate-400">—</span>;
  }
  return (
    <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 ring-1 ring-slate-200/90">
      {text}
    </span>
  );
}

export function RobotForwardTasksPage() {
  const { mpId: mpIdParam, siteId: siteIdParam } = useParams();
  const mpId = Math.max(0, parseInt(mpIdParam || '0', 10) || 0);
  const siteId = Math.max(0, parseInt(siteIdParam || '0', 10) || 0);
  const [mpName, setMpName] = useState('');
  const [sites, setSites] = useState<RobotSiteRow[]>([]);
  const [queueDirectById, setQueueDirectById] = useState<Record<number, number>>({});
  const [queueAggregateById, setQueueAggregateById] = useState<Record<number, number>>({});
  const [robotModuleBySiteId, setRobotModuleBySiteId] = useState<Record<number, boolean>>({});
  const [includesChildSites, setIncludesChildSites] = useState(false);
  const [siteName, setSiteName] = useState('');
  const [data, setData] = useState<TaskRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [notice, setNotice] = useState('');
  const perPage = 15;
  /** 已生效的列表筛选关键字（登记编号精确匹配或标题/正文/编号模糊） */
  const [listQ, setListQ] = useState('');
  const [listQDraft, setListQDraft] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [picker, setPicker] = useState<PickerEntry[]>([]);
  const [pickerTotal, setPickerTotal] = useState(0);
  const [pickerPage, setPickerPage] = useState(1);
  const [pickerQ, setPickerQ] = useState('');
  const [pickerLoading, setPickerLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [removingRobotKey, setRemovingRobotKey] = useState<string | null>(null);

  const loadMpAndSites = useCallback(async () => {
    if (mpId < 1) {
      setMpName('');
      setSites([]);
      return;
    }
    const mp = await apiFetch<MpBriefResp>('get_miniprogram', { method: 'GET', query: { id: mpId } });
    if (!mp.ok || !mp.miniprogram) {
      setMpName('');
      setSites([]);
      return;
    }
    setMpName(mp.miniprogram.name);
    const opts = Array.isArray(mp.miniprogram.entry_site_options) ? mp.miniprogram.entry_site_options : [];
    const scopeIds = new Set<number>(
      opts.length > 0
        ? opts.map((o) => Number(o.id)).filter((x) => x > 0)
        : (mp.miniprogram.site_ids || []).map((x) => Number(x)).filter((x) => x > 0),
    );
    const ls = await apiFetch<{ ok: boolean; items: RobotSiteRow[] }>('list_sites', {
      method: 'GET',
      query: { page: 1, per_page: 500, only_roots: 0 },
    });
    let filtered: RobotSiteRow[] = [];
    if (ls.ok) {
      filtered = ls.items.filter((s) => scopeIds.has(s.id));
      const have = new Set(filtered.map((s) => s.id));
      for (const o of opts) {
        const oid = Number(o.id);
        if (oid > 0 && !have.has(oid)) {
          filtered.push({
            id: oid,
            site_code: '',
            site_name: String(o.site_name ?? `站点 #${oid}`),
            parent_site_id: null,
          });
        }
      }
    } else if (opts.length > 0) {
      filtered = opts.map((o) => ({
        id: Number(o.id),
        site_code: '',
        site_name: String(o.site_name ?? `站点 #${o.id}`),
        parent_site_id: null,
      }));
    }
    setSites(filtered);

    const ids = filtered.map((s) => s.id);
    if (ids.length === 0) {
      setQueueDirectById({});
      setQueueAggregateById({});
      setRobotModuleBySiteId({});
      return;
    }
    const b = await apiFetch<{ ok: boolean; items: RobotSummary[] }>('batch_robot_site_summary', {
      method: 'POST',
      body: JSON.stringify({ site_ids: ids, miniprogram_id: mpId }),
    });
    if (b.ok) {
      const direct: Record<number, number> = {};
      const agg: Record<number, number> = {};
      const moduleOn: Record<number, boolean> = {};
      for (const it of b.items) {
        direct[it.site_id] = it.forward_task_count ?? 0;
        agg[it.site_id] = it.forward_task_count_aggregate ?? it.forward_task_count ?? 0;
        moduleOn[it.site_id] = it.robot_module_enabled === true;
      }
      setQueueDirectById(direct);
      setQueueAggregateById(agg);
      setRobotModuleBySiteId(moduleOn);
    }
  }, [mpId]);

  const siteDisplayRows = useMemo(() => buildRobotSiteDisplayRows(sites), [sites]);

  const siteNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of sites) m.set(s.id, s.site_name);
    return m;
  }, [sites]);

  const loadSiteName = useCallback(() => {
    if (siteId < 1) {
      setSiteName('');
      return;
    }
    const hit = sites.find((x) => x.id === siteId);
    setSiteName(hit?.site_name ?? '');
  }, [siteId, sites]);

  const loadTasks = useCallback(async () => {
    if (siteId < 1 || mpId < 1) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setMsg('');
    setNotice('');
    try {
      const r = await apiFetch<ListTasksResp>('list_forward_tasks', {
        method: 'GET',
        query: {
          site_id: siteId,
          miniprogram_id: mpId,
          page,
          per_page: perPage,
          ...(listQ.trim() ? { q: listQ.trim() } : {}),
        },
      });
      if (r.ok) {
        setData(r.items);
        setTotal(r.total);
        setIncludesChildSites(r.includes_child_sites === true);
      } else {
        setMsg('加载失败');
      }
    } finally {
      setLoading(false);
    }
  }, [siteId, mpId, page, listQ]);

  function runListSearch() {
    setListQ(listQDraft.trim());
    setPage(1);
  }

  function resetListSearch() {
    setListQDraft('');
    setListQ('');
    setPage(1);
  }

  const loadPicker = useCallback(async () => {
    if (siteId < 1 || !addOpen) return;
    setPickerLoading(true);
    try {
      const r = await apiFetch<PickerResp>('list_entries_robot_picker', {
        method: 'GET',
        query: {
          site_id: siteId,
          miniprogram_id: mpId,
          page: pickerPage,
          per_page: 12,
          ...(pickerQ.trim() ? { q: pickerQ.trim() } : {}),
        },
      });
      if (r.ok) {
        setPicker(r.items);
        setPickerTotal(r.total);
      }
    } finally {
      setPickerLoading(false);
    }
  }, [siteId, addOpen, pickerPage, pickerQ]);

  useEffect(() => {
    void loadMpAndSites();
  }, [loadMpAndSites]);

  useEffect(() => {
    loadSiteName();
  }, [loadSiteName]);

  useEffect(() => {
    setPage(1);
  }, [siteId]);

  useEffect(() => {
    void loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    void loadPicker();
  }, [loadPicker]);

  async function addTask(entryId: number) {
    setMsg('');
    const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('add_forward_task', {
      method: 'POST',
      body: JSON.stringify({ site_id: siteId, entry_id: entryId, miniprogram_id: mpId }),
    });
    if (!r.ok) {
      setMsg(forwardTaskErrMessage(r.err, r.detail));
      return;
    }
    setAddOpen(false);
    await loadTasks();
  }

  async function copyHashLink(link: string) {
    setNotice('');
    setMsg('');
    try {
      await navigator.clipboard.writeText(link);
      setNotice('已复制小程序链接到剪贴板');
    } catch {
      setMsg('复制失败，请手动选择链接文字复制');
    }
  }

  async function removeCompletedRobot(
    taskId: number,
    robotId: number,
    robotName: string,
    completionKind: 'groups' | 'moments',
  ) {
    const kindLabel = completionKind === 'moments' ? '发圈' : '群发';
    if (
      !window.confirm(
        `确定将「${robotName}」从「已完成${kindLabel}机器人」中移除？\n该机器人可再次拉取并执行${kindLabel}。`
      )
    ) {
      return;
    }
    const key = `${completionKind}:${taskId}:${robotId}`;
    setRemovingRobotKey(key);
    setMsg('');
    setNotice('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>(
        'remove_forward_task_completed_robot',
        {
          method: 'POST',
          body: JSON.stringify({
            site_id: siteId,
            task_id: taskId,
            robot_id: robotId,
            miniprogram_id: mpId,
            completion_kind: completionKind,
          }),
        }
      );
      if (!r.ok) {
        setMsg(r.detail || r.err || '移除失败');
        return;
      }
      setNotice(`已移除「${robotName}」的${kindLabel}完成记录`);
      await loadTasks();
    } finally {
      setRemovingRobotKey(null);
    }
  }

  function renderCompletedRobotChips(
    row: TaskRow,
    robots: CompletedRobotChip[] | undefined,
    completionKind: 'groups' | 'moments',
  ) {
    const list =
      robots ??
      (completionKind === 'groups'
        ? row.completed_groups_robots ?? row.completed_robots
        : row.completed_moments_robots) ??
      [];
    if (!list.length) {
      const fallbackIds =
        completionKind === 'groups'
          ? row.completed_groups_robot_ids ?? row.completed_robot_ids
          : row.completed_moments_robot_ids ?? [];
      if (fallbackIds.length) {
        return <span className="font-mono text-slate-600">{fallbackIds.join(', ')}</span>;
      }
      return <span className="text-slate-400">—</span>;
    }
    return (
      <ul className="flex max-w-[9.5rem] flex-col gap-1 p-0 list-none m-0">
        {list.map((rb) => {
          const rk = `${completionKind}:${row.id}:${rb.id}`;
          const busy = removingRobotKey === rk;
          return (
            <li
              key={`${completionKind}-${rb.id}`}
              className="flex w-full min-w-0 items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-slate-800 ring-1 ring-slate-200/90"
              title={`点击 × 可移除该机器人的${completionKind === 'moments' ? '发圈' : '群发'}完成记录`}
            >
              <span className="min-w-0 flex-1 truncate text-xs leading-snug">{rb.name}</span>
              <button
                type="button"
                disabled={busy}
                aria-label={`移除 ${rb.name}`}
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-slate-500 transition hover:bg-slate-200 hover:text-slate-900 disabled:opacity-40"
                onClick={() => void removeCompletedRobot(row.id, rb.id, rb.name, completionKind)}
              >
                {busy ? <span className="text-[10px]">…</span> : <span className="text-sm leading-none">×</span>}
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  async function regenerateHashLink(taskId: number, entryId: number) {
    setMsg('');
    setNotice('');
    try {
      const r = await apiFetch<{ ok: boolean; hash_link?: string; err?: string; detail?: string }>(
        'regenerate_forward_task_hash_link',
        {
          method: 'POST',
          body: JSON.stringify({
            site_id: siteId,
            task_id: taskId,
            entry_id: entryId,
            miniprogram_id: mpId,
          }),
        }
      );
      if (r.ok && r.hash_link) {
        setNotice('已补生成小程序短链');
        await loadTasks();
      } else {
        setMsg(r.detail || r.err || '补生成失败，请检查小程序 AppID/Secret');
      }
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '补生成失败');
    }
  }

  async function deleteTask(taskId: number) {
    if (!window.confirm('确定从转发队列中移除此条？机器人将不再拉取该队列项。')) return;
    setDeletingId(taskId);
    setMsg('');
    setNotice('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('delete_forward_task', {
        method: 'POST',
        body: JSON.stringify({ site_id: siteId, task_id: taskId, miniprogram_id: mpId }),
      });
      if (!r.ok) {
        setMsg(r.detail || r.err || '删除失败');
        return;
      }
      await loadTasks();
    } finally {
      setDeletingId(null);
    }
  }

  if (mpId < 1 || siteId < 1) {
    return <p className="text-sm text-rose-700">无效的小程序或站点</p>;
  }

  const pages = Math.max(1, Math.ceil(total / perPage));
  const currentSiteModuleEnabled = robotModuleBySiteId[siteId] === true;

  return (
    <div className="space-y-6">
      <nav className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-slate-500">
        <Link to="/miniprograms" className="rounded-md hover:bg-slate-100 hover:text-teal-800">
          小程序管理
        </Link>
        <span className="text-slate-300" aria-hidden>
          /
        </span>
        <Link to={`/robot-miniprograms/${mpId}`} className="rounded-md hover:bg-slate-100 hover:text-teal-800">
          {mpName || `小程序 #${mpId}`}
        </Link>
        <span className="text-slate-300" aria-hidden>
          /
        </span>
        <span className="font-medium text-slate-800">转发任务</span>
      </nav>

      <div className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight text-slate-900">转发任务</h1>
            <p className="mt-1 text-sm text-slate-600">
              {siteName ? (
                <>
                  当前站点：<span className="font-medium text-slate-800">{siteName}</span>
                  <span className="font-mono text-slate-500">（{siteId}）</span>
                  <span className="mt-1 block text-xs text-teal-800">
                    仅显示小程序「{mpName || `#${mpId}`}」在本{includesChildSites ? '站及下属二级站' : '站'}的转发队列
                  </span>
                  {includesChildSites ? (
                    <span className="mt-0.5 block text-xs text-slate-500">
                      一级站点视图：含下属二级站点队列，仍仅限本小程序
                    </span>
                  ) : null}
                </>
              ) : (
                <span className="font-mono text-slate-600">站点 #{siteId}</span>
              )}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button type="button" variant="primary" onClick={() => setAddOpen(true)}>
              添加任务
            </Button>
            <Link to={`/robot-miniprograms/${mpId}`}>
              <Button type="button" variant="secondary">
                返回小程序管理
              </Button>
            </Link>
          </div>
        </div>

        {siteDisplayRows.length > 0 ? (
          <div className="mt-5 border-t border-slate-100 pt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">切换站点</p>
            <RobotSiteHierarchyChips
              rows={siteDisplayRows}
              activeSiteId={siteId}
              linkForSite={(id) => `/robot-miniprograms/${mpId}/sites/${id}/tasks`}
              queueDirectById={queueDirectById}
              queueAggregateById={queueAggregateById}
            />
          </div>
        ) : null}
      </div>
      {!currentSiteModuleEnabled ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm leading-relaxed text-amber-950">
          本站点在小程序「{mpName || `#${mpId}`}」下<strong className="font-medium">转发模块未开启</strong>
          ，无法将信息加入转发队列。请前往
          <Link to={`/robot-miniprograms/${mpId}`} className="mx-1 font-medium text-teal-800 underline-offset-2 hover:underline">
            小程序机器人管理
          </Link>
          ，在站点列表中对「{siteName || `站点 #${siteId}`}」点击<strong className="font-medium">开启转发</strong>
          （与「转发策略」无关，须单独开启模块开关）。
        </p>
      ) : null}
      {msg ? <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">{msg}</p> : null}
      {notice ? (
        <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">{notice}</p>
      ) : null}
      <Card title="队列中的信息">
        <p className="mb-3 text-sm text-slate-600">
          队列表仅包含归属当前小程序的转发任务；与其他小程序中同一站点的队列相互独立。
        </p>
        <div className="mb-4 flex flex-col gap-3 rounded-xl border border-slate-100 bg-slate-50/50 p-3 sm:flex-row sm:flex-wrap sm:items-end sm:p-4">
          <label className="min-w-0 flex-1 text-sm text-slate-600">
            <span className="mb-1 block text-xs font-medium text-slate-500">筛选</span>
            <input
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 font-mono text-sm shadow-sm outline-none ring-teal-500/20 transition-shadow focus:border-teal-400 focus:ring-2"
              placeholder="登记编号（精确）或标题/正文/编号关键词…"
              value={listQDraft}
              onChange={(e) => setListQDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runListSearch();
              }}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="primary" onClick={runListSearch} disabled={loading}>
              查询
            </Button>
            <Button type="button" variant="secondary" onClick={resetListSearch} disabled={!listQ && !listQDraft.trim()}>
              重置
            </Button>
          </div>
        </div>
        {listQ ? (
          <p className="mb-3 text-xs text-slate-500">
            当前筛选：<span className="font-mono text-slate-700">{listQ}</span>，共 {total} 条
          </p>
        ) : null}
        {loading ? (
          <Spinner label="加载中" />
        ) : data.length === 0 ? (
          <p className="text-sm text-slate-600">
            {listQ.trim()
              ? '无匹配的转发任务，可调整筛选条件或点击「重置」。'
              : '暂无转发任务。若策略为「管理员挑选转发」，请点击「添加任务」从信息列表加入。'}
          </p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600">
                    <th className="px-3 py-3 font-medium">信息</th>
                    <th className="px-3 py-3 font-medium">信息类型</th>
                    {includesChildSites ? <th className="px-3 py-3 font-medium">所属站点</th> : null}
                    <th className="min-w-[12rem] px-3 py-3 font-medium">小程序链接（#）</th>
                    <th className="w-[10rem] max-w-[10rem] px-3 py-3 font-medium">已完成发圈机器人</th>
                    <th className="w-[10rem] max-w-[10rem] px-3 py-3 font-medium">已完成群发机器人</th>
                    <th className="px-3 py-3 font-medium">入队时间</th>
                    <th className="px-3 py-3 text-right font-medium">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {data.map((row) => {
                    const hash = (row.miniprogram_hash_link ?? '').trim();
                    return (
                      <tr key={row.id} className="transition-colors hover:bg-slate-50/80">
                        <td className="px-3 py-3 align-top">
                          <div className="font-mono text-[11px] font-medium text-slate-500">信息 #{row.entry_id}</div>
                          <div className="mt-0.5 max-w-xs font-medium text-slate-900 sm:max-w-md">
                            {(row.title_text || row.serial_number || '').trim() || '（无标题）'}
                          </div>
                          {row.body_text ? (
                            <div className="mt-1 max-w-xs truncate text-xs text-slate-500 sm:max-w-md">{row.body_text}</div>
                          ) : null}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 align-top">
                          {entryTypeBadge(row.type_label, row.type_key)}
                        </td>
                        {includesChildSites ? (
                          <td className="whitespace-nowrap px-3 py-3 align-top text-xs text-slate-700">
                            {(() => {
                              const sid = row.site_id ?? siteId;
                              const nm = siteNameById.get(sid);
                              return nm ? (
                                <span>
                                  {nm}
                                  <span className="ml-1 font-mono text-slate-500">#{sid}</span>
                                </span>
                              ) : (
                                <span className="font-mono">#{sid}</span>
                              );
                            })()}
                          </td>
                        ) : null}
                        <td className="max-w-xs px-3 py-3 align-top sm:max-w-md">
                          {hash ? (
                            <div className="space-y-1.5">
                              <div
                                className="break-all rounded-md bg-slate-50 px-2 py-1.5 font-mono text-[11px] leading-snug text-slate-800 ring-1 ring-slate-200/80"
                                title={hash}
                              >
                                {hash}
                              </div>
                              <Button
                                type="button"
                                variant="secondary"
                                className="!px-2.5 !py-1 text-xs"
                                onClick={() => void copyHashLink(hash)}
                              >
                                复制短链
                              </Button>
                            </div>
                          ) : (
                            <div className="space-y-1.5">
                              <span className="inline-flex rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-900 ring-1 ring-amber-200/80">
                                未生成
                              </span>
                              <Button
                                type="button"
                                variant="secondary"
                                className="!px-2 !py-1 text-xs"
                                onClick={() => void regenerateHashLink(row.id, row.entry_id)}
                              >
                                补生成短链
                              </Button>
                            </div>
                          )}
                        </td>
                        <td className="w-[10rem] max-w-[10rem] px-3 py-3 align-top text-xs text-slate-700">
                          {renderCompletedRobotChips(row, row.completed_moments_robots, 'moments')}
                        </td>
                        <td className="w-[10rem] max-w-[10rem] px-3 py-3 align-top text-xs text-slate-700">
                          {renderCompletedRobotChips(row, row.completed_groups_robots ?? row.completed_robots, 'groups')}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 align-top text-xs text-slate-600">{row.created_at}</td>
                        <td className="px-3 py-3 align-top text-right">
                          <Button
                            type="button"
                            variant="danger"
                            className="!px-2.5 !py-1.5 text-xs"
                            disabled={deletingId === row.id}
                            onClick={() => void deleteTask(row.id)}
                          >
                            {deletingId === row.id ? '删除中…' : '移出队列'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {total > 0 ? (
          <PaginationBar
            className="mt-4"
            page={page}
            totalPages={pages}
            total={total}
            summary={`共 ${total} 条，每页 ${perPage} 条`}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(pages, p + 1))}
          />
        ) : null}
      </Card>

      {addOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" role="dialog">
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="border-b border-slate-100 px-5 py-3">
              <h3 className="text-lg font-semibold text-slate-900">选择信息加入转发队列</h3>
              <div className="mt-2 flex gap-2">
                <input
                  className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  placeholder="搜索标题、正文、编号…"
                  value={pickerQ}
                  onChange={(e) => {
                    setPickerQ(e.target.value);
                    setPickerPage(1);
                  }}
                />
                <Button type="button" variant="secondary" onClick={() => void loadPicker()}>
                  搜索
                </Button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
              {pickerLoading ? (
                <Spinner label="加载信息" />
              ) : picker.length === 0 ? (
                <p className="text-sm text-slate-600">没有可展示的信息。</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {picker.map((e) => (
                    <li key={e.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-mono text-xs text-slate-500">登记 #{e.id}</div>
                          {entryTypeBadge(e.type_label, e.type_key)}
                        </div>
                        <div className="truncate font-medium text-slate-800">
                          {(e.title_text || e.serial_number || '').trim() || '（无标题）'}
                        </div>
                      </div>
                      <Button type="button" variant="primary" className="shrink-0" onClick={() => void addTask(e.id)}>
                        加入转发
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-5 py-3">
              {pickerTotal > 0 ? (
                <PaginationBar
                  page={pickerPage}
                  totalPages={Math.max(1, Math.ceil(pickerTotal / 12))}
                  total={pickerTotal}
                  onPrev={() => setPickerPage((p) => Math.max(1, p - 1))}
                  onNext={() => setPickerPage((p) => Math.min(Math.max(1, Math.ceil(pickerTotal / 12)), p + 1))}
                />
              ) : null}
              <Button type="button" variant="secondary" onClick={() => setAddOpen(false)}>
                关闭
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
